import { isEmpty, isNil, compact } from 'lodash/fp';
import {
    condition,
    from,
    group,
    select,
    sql,
    value,
    where,
    generateRangeConditions
} from 'app/lib/utils/sql_generator';

export const warehouseUsageSql = (accountName) => `
WITH
    "COMPUTE_RATE_HISTORY" AS (
        SELECT
            *
        FROM
            SNOWFLAKE.ORGANIZATION_USAGE.RATE_SHEET_DAILY MATCH_RECOGNIZE (
            PARTITION BY
            ACCOUNT_NAME,
            USAGE_TYPE
            ORDER BY
            "DATE" MEASURES FIRST ("DATE") AS START_DATE_INCLUSIVE,
            ANY_VALUE(EFFECTIVE_RATE) as EFFECTIVE_RATE
            ONE ROW PER MATCH
            AFTER MATCH SKIP PAST LAST ROW
            PATTERN (row_with_no_change+ row_with_change)
            DEFINE
            row_with_no_change AS EFFECTIVE_RATE = LEAD(EFFECTIVE_RATE),
            row_with_change AS EFFECTIVE_RATE <> LEAD(EFFECTIVE_RATE)
        )
    ),
    "RATE_LOOKUP" AS (
        SELECT
            ACCOUNT_NAME,
            USAGE_TYPE,
            START_DATE_INCLUSIVE,
            COALESCE(
                (
                LEAD(START_DATE_INCLUSIVE) OVER (
                    PARTITION BY
                    USAGE_TYPE
                    ORDER BY
                    START_DATE_INCLUSIVE
                )
                ) - INTERVAL '1 DAY',
                CURRENT_DATE()
            ) AS END_DATE_INCLUSIVE,
            EFFECTIVE_RATE
        FROM
            COMPUTE_RATE_HISTORY
        WHERE
            ACCOUNT_NAME = '${accountName}'
    ),
    "CLOUD_SERVICE_ADJUSTMENTS" AS (
        SELECT
            USAGE_DATE,
            CREDITS_ADJUSTMENT_CLOUD_SERVICES as CLOUD_SERVICE_CREDIT_ADJUSTMENT
        FROM SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY
        WHERE SERVICE_TYPE = 'WAREHOUSE_METERING'
    ),
    "METERING_HISTORY_WITH_RATES" AS (
        SELECT
            START_TIME,
            END_TIME,
            WAREHOUSE_NAME,
            TO_DATE(DATE_TRUNC(month, START_TIME)) as USAGE_MONTH,
            CREDITS_USED_COMPUTE,
            CREDITS_USED_CLOUD_SERVICES,
            CR.EFFECTIVE_RATE AS COMPUTE_EFFECTIVE_RATE,
            CSR.EFFECTIVE_RATE as CLOUD_SERVICES_EFFECTIVE_RATE
        FROM
            SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY as WMH
            JOIN RATE_LOOKUP as CR
                ON DATE_TRUNC(DAY, WMH.START_TIME) BETWEEN CR.START_DATE_INCLUSIVE AND CR.END_DATE_INCLUSIVE
                AND CR.USAGE_TYPE = 'compute'
            JOIN RATE_LOOKUP as CSR
                ON DATE_TRUNC(DAY, WMH.START_TIME) BETWEEN CSR.START_DATE_INCLUSIVE AND CSR.END_DATE_INCLUSIVE
                AND CSR.USAGE_TYPE = 'cloud services'
        WHERE
            -- Only return up to 12 months worth of data since snowflake returns more
            DATE_TRUNC(MONTH, START_TIME) > DATE_TRUNC(MONTH, CURRENT_TIMESTAMP() - INTERVAL '12 months')
            AND WAREHOUSE_NAME NOT ILIKE '%TEST%'
    ),
    "ACCOUNT_ADJUSTMENTS_BY_MONTH" AS (
        SELECT
            DATE_TRUNC(MONTH, USAGE_DATE) as USAGE_MONTH,
            SUM(AR.EFFECTIVE_RATE * CSA.CLOUD_SERVICE_CREDIT_ADJUSTMENT) as MONTHLY_ADJUSTMENT
        FROM
            CLOUD_SERVICE_ADJUSTMENTS as CSA
            JOIN RATE_LOOKUP as AR
                ON DATE_TRUNC(DAY, CSA.USAGE_DATE) BETWEEN AR.START_DATE_INCLUSIVE AND AR.END_DATE_INCLUSIVE
                AND AR.USAGE_TYPE = 'adj for incl cloud services'
        WHERE
            DATE_TRUNC(MONTH, USAGE_DATE) > DATE_TRUNC(MONTH, CURRENT_TIMESTAMP() - INTERVAL '12 months')
        GROUP BY
            1
    ),
    "ACCOUNT_SPEND_BY_MONTH" AS (
        SELECT
            USAGE_MONTH,
            SUM((CREDITS_USED_COMPUTE * COMPUTE_EFFECTIVE_RATE) + (CREDITS_USED_CLOUD_SERVICES * CLOUD_SERVICES_EFFECTIVE_RATE)) AS MONTHLY_SPEND
        FROM
            METERING_HISTORY_WITH_RATES
        GROUP BY
            1
    )
SELECT
    WAREHOUSE_NAME,
    USAGE_MONTH,
    SUM((CREDITS_USED_COMPUTE * COMPUTE_EFFECTIVE_RATE) + (CREDITS_USED_CLOUD_SERVICES * CLOUD_SERVICES_EFFECTIVE_RATE)) AS MONTHLY_SPEND
FROM
    METERING_HISTORY_WITH_RATES
GROUP BY
    1,
    2
UNION ALL
SELECT
    '<ACCOUNT-LEVEL>' as WAREHOUSE_NAME,
    s.USAGE_MONTH,
    s.MONTHLY_SPEND + a.MONTHLY_ADJUSTMENT
FROM
    ACCOUNT_SPEND_BY_MONTH as s
    JOIN ACCOUNT_ADJUSTMENTS_BY_MONTH as a ON s.USAGE_MONTH = a.USAGE_MONTH
`;

const generateUserConditions = (userFilter, dimensions) => {
    if (isEmpty(userFilter)) {
        return condition(
            'QUERY_USER_NAME',
            dimensions.includes('USER') ? 'IS NOT NULL' : 'IS NULL'
        );
    }

    return condition('QUERY_USER_NAME', 'IN', value(userFilter));
};

const generateWarehouseConditions = (warehouseFilter, dimensions) => {
    if (isEmpty(warehouseFilter)) {
        return condition(
            'WAREHOUSE_NAME',
            dimensions.includes('WAREHOUSE') ? "NOT LIKE '%TEST%'" : 'IS NULL'
        );
    }

    return condition('WAREHOUSE_NAME', 'IN', value(warehouseFilter));
};

const hasSecondaryDimension = (value) => !isNil(value) && value !== '';

const COLUMN_MAPPINGS = {
    'WAREHOUSE': 'WAREHOUSE_NAME',
    'USER': 'QUERY_USER_NAME',
    'QUERY TIME': 'QUERY_TOTAL_ELAPSED_TIME',
    'CLOUD SERVICES CREDITS': 'QUERY_TOTAL_CREDITS_USED_CLOUD_SERVICES',
    'COMPUTE CREDITS': 'WAREHOUSE_TOTAL_CREDITS_USED_COMPUTE'
};

const generateDimensionColumns = (dateBin, secondaryDimension, primaryDimension) => {
    const columns =
        primaryDimension === 'DATE'
            ? [`DATE_TRUNC(${dateBin}, WAREHOUSE_METERING_HOUR) AS "TIMESTAMP"`]
            : [COLUMN_MAPPINGS[primaryDimension]];

    if (hasSecondaryDimension(secondaryDimension)) {
        columns.push(COLUMN_MAPPINGS[secondaryDimension]);
    }

    return columns;
};

const generateMeasureColumn = (selectedMeasure) => {
    const column = COLUMN_MAPPINGS[selectedMeasure];

    if (column) {
        return `SUM(${column}) AS "${selectedMeasure}"`;
    }

    return `SUM(QUERY_COUNT) AS "QUERY COUNT"`;
};

export const warehouseCubeSql = ({
    fqn,
    dateFilter,
    warehouseFilter,
    userFilter,
    primaryDimension = 'DATE',
    secondaryDimension,
    selectedMeasure,
    dateBin = 'WEEK'
}) =>
    sql(
        select(
            ...generateDimensionColumns(dateBin, secondaryDimension, primaryDimension),
            generateMeasureColumn(selectedMeasure)
        ),
        from(fqn),
        where(
            generateRangeConditions(dateFilter, 'WAREHOUSE_METERING_HOUR'),
            generateUserConditions(userFilter, compact([secondaryDimension, primaryDimension])),
            generateWarehouseConditions(
                warehouseFilter,
                compact([secondaryDimension, primaryDimension])
            )
        ),
        group(1, hasSecondaryDimension(secondaryDimension) ? 2 : null)
    );
